import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import type { TusUploadHandle } from "@/lib/tusUpload";

export type UploadStatus = "uploading" | "done" | "error";

export type UploadRecord = {
  id: string;
  fileName: string;
  isVideo: boolean;
  dogName: string;
  pct: number;
  status: UploadStatus;
  error?: string;
};

type StartUploadParams = {
  files: File[];
  // bookingId null = Daycare-Abo-Tag (daycare_attendance ohne adventure_bookings-Zeile).
  // walk_photos.booking_id ist nullable; Versand + Kunden-Galerie hängen nur am dog_id.
  dog: { bookingId: string | null; dogId: string; dogName: string };
  employeeId: string;
  /** Wie viele Hunde dieselben Dateien in diesem Batch bekommen (Walker: „auch an weitere
   *  Hunde senden"). 1 = Foto eindeutig diesem Hund → darf Avatar werden (DB-Trigger);
   *  >1 = Gruppenbild → kein Avatar. */
  batchDogCount?: number;
  /** walk_photos.photo_kind — 'daycare_single' = Einzelbild fuer den Nintendogs-Avatar
   *  (ab dem 5. reiht der DB-Trigger die Auffrischung ein). Ohne Angabe: DB-Default 'gassi'. */
  photoKind?: "gassi" | "daycare_group" | "daycare_single";
  /** walk_photos.huta_category — welches WhatsApp-Template n8n fuer diesen Upload waehlt
   *  (Daycarebild/Gassibild/Fuetterungsbild/Siestabild aus dem Walker-HuTa-Tab). Orthogonal
   *  zu photoKind. Nur vom HuTa-Tab gesetzt; Gassi-Uploads lassen dies weg → DB bleibt NULL. */
  hutaCategory?: "daycare" | "gassi" | "fuetterung" | "siesta";
  onBatchComplete: (result: { successCount: number; failCount: number }) => void;
};

const isVideoFile = (file: File) => file.type.startsWith("video/");

class WalkUploadManager {
  private uploads = new Map<string, UploadRecord>();
  private handles = new Map<string, TusUploadHandle>();
  private listeners = new Set<() => void>();
  // useSyncExternalStore requires a stable reference when nothing changed —
  // recomputing a fresh array on every getSnapshot() call causes an infinite
  // render loop, so this is only rebuilt inside notify().
  private cachedSnapshot: UploadRecord[] = [];

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): UploadRecord[] => this.cachedSnapshot;

  private notify() {
    this.cachedSnapshot = Array.from(this.uploads.values());
    for (const listener of this.listeners) listener();
  }

  private setRecord(id: string, patch: Partial<UploadRecord>) {
    const existing = this.uploads.get(id);
    if (!existing) return;
    this.uploads.set(id, { ...existing, ...patch });
    this.notify();
  }

  cancelUpload = async (id: string) => {
    const handle = this.handles.get(id);
    if (handle) await handle.abort();
    this.uploads.delete(id);
    this.handles.delete(id);
    this.notify();
  };

  startUpload = ({ files, dog, employeeId, photoKind, hutaCategory, batchDogCount = 1, onBatchComplete }: StartUploadParams) => {
    const today = format(new Date(), "yyyy-MM-dd");

    const tasks = files.map((file) => {
      const id = crypto.randomUUID();
      const isVideo = isVideoFile(file);
      this.uploads.set(id, {
        id,
        fileName: file.name,
        isVideo,
        dogName: dog.dogName,
        pct: 0,
        status: "uploading",
      });
      return { id, file, isVideo };
    });
    this.notify();

    (async () => {
      const results = await Promise.allSettled(
        tasks.map(async ({ id, file, isVideo }) => {
          try {
            let processedFile: File | Blob = file;
            if (!isVideo) {
              const { compressImage } = await import("@/lib/imageCompression");
              processedFile = await compressImage(file, { maxWidth: 1920, maxHeight: 1920, quality: 0.85 });
            } else {
              // Standort/Gerät/Aufnahmezeit entfernen, ohne neu zu kodieren (Befund 15.10.2026).
              // Lazy; scheitert schon das Laden, geht das Original hoch wie bisher.
              processedFile = await import("@/lib/videoMetadaten").then(
                (m) => m.videoOhneMetadaten(file),
                () => file,
              );
            }

            const ext = isVideo ? "mp4" : "jpg";
            const contentType = isVideo ? file.type : "image/jpeg";
            const objectName = `${today}/${dog.dogId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;

            const { tusUploadFile } = await import("@/lib/tusUpload");
            const handle = tusUploadFile({
              file: processedFile,
              bucketName: "walk-photos",
              objectName,
              contentType,
              onProgress: (pct) => this.setRecord(id, { pct }),
            });
            this.handles.set(id, handle);
            await handle.promise;

            const { data: urlData, error: urlError } = await supabase.storage
              .from("walk-photos")
              .createSignedUrl(objectName, 31536000);
            if (urlError || !urlData?.signedUrl) throw new Error("Fehler beim Erstellen der Foto-URL");

            const photoId = crypto.randomUUID() as string;
            // photo_kind/huta_category sind (noch) nicht in den generierten Types → Cast (Konvention).
            const { error: insertError } = await supabase.from("walk_photos").insert({
              id: photoId,
              booking_id: dog.bookingId,
              dog_id: dog.dogId,
              employee_id: employeeId,
              photo_url: urlData.signedUrl,
              sent_via_whatsapp: false,
              whatsapp_status: "pending",
              ...(photoKind ? { photo_kind: photoKind } : {}),
              ...(hutaCategory ? { huta_category: hutaCategory } : {}),
              // Gruppenbild (>1) wird vom DB-Trigger nicht zum Avatar gemacht.
              batch_dog_count: batchDogCount,
            } as any);
            if (insertError) throw new Error("Fehler beim Speichern");

            this.setRecord(id, { pct: 100, status: "done" });
            return { photoId, signedUrl: urlData.signedUrl };
          } catch (err) {
            this.setRecord(id, {
              status: "error",
              error: err instanceof Error ? err.message : "Fehler beim Hochladen",
            });
            throw err;
          } finally {
            this.handles.delete(id);
          }
        }),
      );

      const succeeded = results.filter(
        (r): r is PromiseFulfilledResult<{ photoId: string; signedUrl: string }> => r.status === "fulfilled",
      );
      const failCount = results.length - succeeded.length;

      if (succeeded.length > 0) {
        try {
          const { data: webhookData, error: webhookError } = await supabase.functions.invoke(
            "walk-photo-webhook",
            {
              body: {
                dog_id: dog.dogId,
                photo_urls: succeeded.map((r) => r.value.signedUrl),
                photo_ids: succeeded.map((r) => r.value.photoId),
              },
            },
          );
          // kanal "app" = WhatsApp-Weiche: Foto ging nur in die App, der Server hat den Status
          // schon auf 'app' gesetzt. Nicht als WhatsApp-gesendet stempeln.
          if (!webhookError && webhookData?.success && webhookData?.kanal !== "app") {
            // Genau die eben geschriebenen Zeilen stempeln. Vorher lief das über
            // booking_id+dog_id — bei booking_id NULL (Daycare-Abo-Tag) matcht
            // PostgREST `booking_id=eq.null` keine Zeile, das Foto bliebe ewig 'pending'.
            await supabase
              .from("walk_photos")
              .update({ sent_via_whatsapp: true, sent_at: new Date().toISOString(), whatsapp_status: "sent" })
              .in("id", succeeded.map((r) => r.value.photoId))
              // Hat foto-versand schon gesendet, stünde sonst ein „delivered“ wieder auf „sent“.
              .is("twilio_message_sid", null);
          }
        } catch (webhookError) {
          console.warn("Webhook error:", webhookError);
        }
      }

      // Clear finished records after a short delay so the UI can show "done" briefly.
      setTimeout(() => {
        tasks.forEach(({ id }) => this.uploads.delete(id));
        this.notify();
      }, 2000);

      onBatchComplete({ successCount: succeeded.length, failCount });
    })();
  };
}

export const walkUploadManager = new WalkUploadManager();
